<template>
<div
  ref='resource-overlay'
  class='resource-overlay'
>
  <div
    v-if='!resource'
    class='resource-overlay_inner'
  >
    <button
      ref='close-button'
      class='resource-overlay_close'
      @click='closeResourse'
    >
      <CloseIcon />
      {{ $t('close') }}
    </button>

    <PageLoader class='page-loader--overlay' />
  </div>

  <!-- Generic Resource -->
  <transition
    mode='out-in'
    name='fast-fade'
  >
  <div
    v-if='resource && !is_article'
    :style='background_style'
    class='resource-overlay_inner'
  >
    <button
      ref='close-button'
      class='resource-overlay_close'
      @click='closeResourse'
      @blur='checkFocus'
    >
      <CloseIcon />
      {{ $t('close') }}
    </button>

    <div class='resource-overlay_type copy-three'>
      {{ $tc('resource', 1) }}
    </div>

    <aside
      v-if="is_audio"
      class='resource-overlay_aside'
    >
      <img
        :src='resource.activity.card_image'
        alt=''
        class='resource-overlay_image'
      >
    </aside>

    <main class='resource-overlay_main'>
      <div class='resource-overlay_headline heading-one'>
        {{ resource.headline }}
      </div>

      <div class='resource-overlay_subheadline heading-five'>
        {{ resource.activity.subheadline }}
      </div>

      <a
        v-if='resource.activity.link'
        ref='last-action'
        :href='resource.activity.link.href'
        class='resource-overlay_link action-one-branded'
        target="_blank"
        @click.stop='resourceClick'
      >
        {{ resource.activity.link.title }}
      </a>

      <!-- Audio Resource -->
      <div
        v-if='is_audio'
        class='resource-overlay_audio-container'
      >
        <AudioPlayer
          :audio_image='resource.activity.card_image'
          :audio_playlist_title='$tc("resource", 2)'
          :audio_title='resource.headline'
          :audio_id='resource.activity.id'
          :file_url='resource.activity.provider_url'
        />
      </div>

      <!-- Video Resource -->
      <div
        v-if='is_video'
        class='resource-overlay_video-container'
      >
        <div
          ref='video'
          class='resource-overlay_video'
        />

        <div
          v-if='resource.activity.provider === "wistia"'
          ref='video-play'
          class='resource-overlay_video-play'
          @click='play'
        >
          <i />
        </div>
      </div>
    </main>
  </div>
  </transition>

  <!-- Article -->
  <transition
    mode='out-in'
    name='fast-fade'
  >
  <div
    v-if='resource && is_article'
    class="article-overlay"
  >
    <button
      ref='close-button'
      class='resource-overlay_close'
      @click='closeResourse'
    >
      <CloseIcon />
      {{ $t('close') }}
    </button>

    <div
      :style='background_style'
      class='article-overlay_top'
    >
      <div class='article-overlay_top_inner'>
        <span class='article-overlay_type copy-three'>{{ resource.activity.subheadline || $t('article') }}</span>
        <h2
          v-if='resource.headline'
          class='article-overlay_headline heading-one'
        >
          {{ resource.headline }}
        </h2>
      </div>
    </div>

    <div
      class='article-overlay_body'
      v-html='resource.activity.content'
    />

    <div class='article-overlay_bottom'>
      <button
        ref='last-action'
        class='article-overlay_keep button-one-branded'
        @click='closeResourse'
      >
        {{ $t('keep_learning') }}
      </button>
    </div>
  </div>
  </transition>

  <div
    class='resource-overlay_restricter'
    tabindex='0'
    @focus="setFocus('close-button')"
  />
</div>
</template>

<script>
import { useHomeStore } from 'home/stores/home'
import { mapActions } from 'pinia'

import TbkVideo from 'tbk-utils/video/tbk-video'
// import CommonMixin from "lesson/components/slide/common-mixin.js"
import AudioPlayer from 'components/Shared/AudioPlayer/AudioPlayerInline'
import PageLoader from 'components/PageLoader/PageLoader'
import CloseIcon from '../icons/close.svg'

export default {
  props: ['resource', 'closeOverlay'],

  components: {
    AudioPlayer,
    PageLoader,
    CloseIcon
  },

  data() {
    return {
      shift_is_pressed: false,
      keyUpCallback: (e) => this.detectKeyup(e),
      keyDownCallback: (e) => this.detectKeydown(e),
      lastFocusedSelector: null
    }
  },

  created() {
    $('body').addClass('popup-opened')
  },

  mounted() {
    // Store classes of the element that initiated the overlay
    const whatClasses = $('html').attr('data-whatclasses')
    let lastFocusedClasses = []
    if (whatClasses) lastFocusedClasses = whatClasses.split(',')
    if (lastFocusedClasses.length) {
      this.lastFocusedSelector = '.' + lastFocusedClasses.join('.')
    }

    this.setFocus('close-button')
    document.body.addEventListener('keyup', this.keyUpCallback)
    document.body.addEventListener('keydown', this.keyDownCallback)

    if (this.resource && this.is_video) {
      if (this.resource.activity.provider === 'embed') {
        this._loadAdditionalWistiaScriptsAndInit()
      } else {
        this.getEmbedCode()
      }
    }

    if (this.pausePlayback) {
      this.pausePlayback()   // pause the sticky player (if it plays)
    }
  },

  beforeUpdate() {
    // Required when overlay was opened before resource was loaded
    if (this.resource && this.is_video) {
      if (this.resource.activity.provider === 'embed') {
        this._loadAdditionalWistiaScriptsAndInit()
      } else {
        this.getEmbedCode()
      }
    }
  },

  beforeUnmount() {
    document.body.removeEventListener('keyup', this.keyUpCallback)
    document.body.removeEventListener('keydown', this.keyDownCallback)
    // some workarouds related to resource carousel (TBK-3708)
    if (this.lastFocusedSelector === '.resources-carousel_resource') {
      $('.part_next-slide').focus()
    } else {
      $(this.lastFocusedSelector).focus()
    }
    $('body').removeClass('popup-opened')
  },

  computed: {
    background_style() {
      // Background image
      if (this.resource.activity.background_image) {
        return `backgroundImage: url(${this.resource.activity.background_image})`
      }

      // Gradient
      if (!this.resource.activity.background_image && this.resource.activity.gradient_start) {
        const start = this.resource.activity.gradient_start
        const end = this.resource.activity.gradient_end || this.resource.activity.gradient_start
        const gradient = `linear-gradient(137deg, ${start} 0%, ${end} 92%)`
        return 'backgroundImage: ' +  gradient
      } else {
        return ''
      }
    },

    is_article() {
      return this.resource.activity.type === 'activity_v1_articles'
    },

    is_audio() {
      return this.resource.activity.type === 'activity_v1_audios'
    },

    is_video() {
      return this.resource.activity.type === 'activity_v1_videos'
    }
  },

  methods: {
    ...mapActions(useHomeStore, [
      'pausePlayback'
    ]),

    resourceClick() {
      if (typeof (dataLayer) === 'undefined') return

      const url = this.resource.type === 'activity_v1_articles' ? window.location.href : this.resource.activity.link.href
      const destination = url.search(window.location.hostname) > 0 || /^\//.test(this.resource.activity.link.href) ? 'internal' : 'external'
      let category = this.$route ? this.$route.name : null

      if (category === 'course') {
        category = 'courses'
      }

      const gtm_event = {
        event:                'resourceClick',
        pageCategory:         category,
        resourceTitle:        this.resource.headline,
        resourceSubTitle:     this.resource.activity.link ? this.resource.activity.link.title : this.resource.cta,
        resourceID:           this.resource.id,
        resourceURL:          url,
        resourceDestination:  destination
      }

      dataLayer.push(gtm_event)
    },

    getEmbedCode() {
      const provider = this.resource.activity.provider
      const code = this.resource.activity.code

      this.vClient = TbkVideo.new(provider, code)

      this.vClient.getEmbedCode((code, video) => {
        $(this.$refs.video).html(code)
      })
    },

    closeResourse(event) {
      if (event.currentTarget.className.match(/resource-overlay_close/g) ||
          event.currentTarget.className.match(/article-overlay_keep/g)) {
        this.closeOverlay()
      }
    },

    detectKeyup(e) {
      if ((e.key !== undefined && e.key === 'Escape') ||
        (e.keyCode !== undefined && e.keyCode === 27)) {
        this.closeOverlay()
      }

      if (e.key === 'Shift') {
        this.shift_is_pressed = false
      }
    },

    detectKeydown(e) {
      if (e.key === 'Shift') {
        this.shift_is_pressed = true
      }
    },

    setFocus(ref_name) {
      this.$refs[ref_name].focus()
    },

    checkFocus(e) {
      if (this.shift_is_pressed) {
        this.setFocus('last-action')
      }
    },

    play() {
      /* global Wistia */
      Wistia.api(this.resource.activity.code).play()
      $(this.$refs['video-play']).hide()
    },

    _loadAdditionalWistiaScriptsAndInit() {
      // Load Wistia API script
      if (document.querySelector('#wistia_api_script') === null) {
        const wistia_api_script = document.createElement('script')
        wistia_api_script.setAttribute('src', '//fast.wistia.com/assets/external/E-v1.js')
        wistia_api_script.async = true
        wistia_api_script.setAttribute('id', 'wistia_api_script')
        document.head.appendChild(wistia_api_script)
      }

      // Load Wistia Playlist API script
      if (document.querySelector('#wistia_playlist_api_script') === null) {
        const wistia_playlist_api_script = document.createElement('script')
        wistia_playlist_api_script.setAttribute('src', '//fast.wistia.com/assets/external/playlist-v1.js')
        wistia_playlist_api_script.async = true
        wistia_playlist_api_script.setAttribute('id', 'wistia_playlist_api_script')
        document.head.appendChild(wistia_playlist_api_script)
      }

      setTimeout(() => {
        this.getEmbedCode()
      }, 1000)
    }
  }

}
</script>

<style lang='sass' scoped>
  @import './ResourceOverlay'
</style>
